<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
  >
    <div class="rounded-t bg-white mb-0 px-6 py-6">
      <h6 class="text-blueGray-700 text-xl font-bold">
        {{ $t("password.changeTitle") }}
      </h6>
    </div>
    <div class="flex-auto px-4 lg:px-10 py-10 pt-0">
      <form @submit="handleSubmit" autocomplete="off">
        <div class="flex flex-wrap mt-6">
          <div class="w-full lg:w-6/12 px-4">
            <div class="relative w-full my-4">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                for="current-password"
              >
                {{ $t("password.current") }}
                <span :title="$t('Este campo es requerido')">
                  <span class="sr-only">
                    {{ $t("Este campo es requerido") }}</span
                  >
                  <i class="fas fa-asterisk"></i>
                </span>
              </label>
              <input
                id="current-password"
                :type="showPasswords ? 'text' : 'password'"
                autocomplete="current-password"
                required
                v-model="currentPassword"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </div>

          <div class="w-full lg:w-6/12 px-4"></div>

          <div class="w-full lg:w-6/12 px-4">
            <div class="relative w-full my-4">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                for="new-password"
              >
                {{ $t("password.new") }}
                <span :title="$t('Este campo es requerido')">
                  <span class="sr-only">
                    {{ $t("Este campo es requerido") }}</span
                  >
                  <i class="fas fa-asterisk"></i>
                </span>
              </label>
              <input
                id="new-password"
                :type="showPasswords ? 'text' : 'password'"
                autocomplete="new-password"
                required
                v-model="newPassword"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
              <PasswordChecklist :password="newPassword" :context="context" />
            </div>
          </div>

          <div class="w-full lg:w-6/12 px-4">
            <div class="relative w-full my-4">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                for="repeat-password"
              >
                {{ $t("password.repeat") }}
                <span :title="$t('Este campo es requerido')">
                  <span class="sr-only">
                    {{ $t("Este campo es requerido") }}</span
                  >
                  <i class="fas fa-asterisk"></i>
                </span>
              </label>
              <input
                id="repeat-password"
                :type="showPasswords ? 'text' : 'password'"
                autocomplete="new-password"
                required
                v-model="repeatPassword"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
              <p
                class="mt-2 text-xs text-red-500"
                v-if="repeatPassword && !passwordsMatch"
              >
                {{ $t("password.mismatch") }}
              </p>
            </div>
          </div>

          <div class="w-full px-4">
            <label class="inline-flex items-center cursor-pointer text-xs">
              <input
                type="checkbox"
                v-model="showPasswords"
                class="form-checkbox border-0 rounded text-blueGray-700 ml-1 w-5 h-5 ease-linear transition-all duration-150"
              />
              <span class="ml-2 text-blueGray-600">
                {{ $t("password.show") }}
              </span>
            </label>
          </div>

          <div class="w-full px-4 mt-4 text-right">
            <button
              class="bg-blueGray-800 text-white bg-emerald-500 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
              type="submit"
              :disabled="waitingForSubmit"
            >
              {{ $t("password.submit") }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { useToast } from "vue-toastification";
import PasswordChecklist from "@/components/Forms/PasswordChecklist.vue";
import { isPasswordValid } from "@/utils/passwordPolicy";
import UsuarioService from "@/services/usuario.service";

export default {
  components: {
    PasswordChecklist,
  },

  data() {
    return {
      currentPassword: "",
      newPassword: "",
      repeatPassword: "",
      showPasswords: false,
      waitingForSubmit: false,
    };
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
    }),

    context() {
      return {
        nombre: this.userInfo?.nombre,
        mail: this.userInfo?.mail,
      };
    },

    passwordsMatch() {
      return this.newPassword === this.repeatPassword;
    },

    // El botón no se deshabilita: si algo no cumple, al enviar se avisa por qué.
    validationError() {
      if (!this.currentPassword || !this.newPassword) {
        return "password.incomplete";
      }

      if (!isPasswordValid(this.newPassword, this.context)) {
        return "password.invalid";
      }

      if (!this.passwordsMatch) {
        return "password.mismatch";
      }

      if (this.newPassword === this.currentPassword) {
        return "password.sameAsCurrent";
      }

      return null;
    },
  },

  methods: {
    reset() {
      this.currentPassword = "";
      this.newPassword = "";
      this.repeatPassword = "";
      this.showPasswords = false;
    },

    handleSubmit(event) {
      event.preventDefault();
      const toast = useToast();

      const error = this.validationError;

      if (error) {
        toast.error(this.$t(error));
        return;
      }

      this.waitingForSubmit = true;

      UsuarioService.changePassword({
        currentPassword: this.currentPassword,
        newPassword: this.newPassword,
      })
        .then(() => {
          this.reset();
          toast.success(this.$t("password.changed"));
        })
        .catch((error) => {
          // El error del backend ya viene en texto: no se loguea nada del form.
          toast.error(
            typeof error === "string" ? error : this.$t("password.error")
          );
        })
        .finally(() => {
          this.waitingForSubmit = false;
        });
    },
  },
};
</script>
